import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Plus, Package, Search, Filter, Eye, Edit, Trash2, Calendar, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { purchaseOrdersAPI, suppliersAPI, inventoryAPI } from '../../lib/api';

const PurchaseOrdersPage = () => {
  const { user } = useAuth();
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPO, setSelectedPO] = useState(null);
  
  const [formData, setFormData] = useState({
    supplier_id: '',
    items: [{ _uid: crypto.randomUUID(), item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }],
    notes: '',
    expected_delivery: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {      
      const [posRes, suppliersRes, inventoryRes] = await Promise.all([
        purchaseOrdersAPI.getAll(),
        suppliersAPI.getAll(),
        inventoryAPI.getAll()
      ]);
      
      setPurchaseOrders(posRes.data || []);
      setSuppliers(suppliersRes.data || []);
      setInventory(inventoryRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      if (error.response?.status !== 401) toast.error('Failed to load purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePO = async (e) => {
    e.preventDefault();
    
    if (!formData.supplier_id) {
      toast.error('Please select a supplier');
      return;
    }
    
    if (formData.items.length === 0 || !formData.items[0].item_id) {
      toast.error('Please add at least one item');
      return;
    }
    
    try {
      const response = await purchaseOrdersAPI.create(formData);
      setPurchaseOrders([response.data, ...purchaseOrders]);
      toast.success('Purchase order created successfully');
      setCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create PO:', error);
      toast.error(error.response?.data?.detail || 'Failed to create purchase order');
    }
  };

  const handleUpdateStatus = async (poId, newStatus) => {
    try {
      const updateData = { status: newStatus };
      
      if (newStatus === 'received') {
        updateData.received_date = new Date().toISOString();
      }
      
      await purchaseOrdersAPI.update(poId, updateData);
      
      setPurchaseOrders(purchaseOrders.map(po => 
        po.id === poId ? { ...po, status: newStatus, received_date: updateData.received_date } : po
      ));
      
      toast.success('Status updated successfully');
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleDeletePO = async (poId) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    
    try {
      await purchaseOrdersAPI.delete(poId);
      setPurchaseOrders(purchaseOrders.filter(po => po.id !== poId));
      toast.success('Purchase order deleted');
    } catch (error) {
      console.error('Failed to delete PO:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete purchase order');
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { _uid: crypto.randomUUID(), item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    if (field === 'item_id') {
      const item = inventory.find(inv => inv.id === value);
      if (item) {
        newItems[index].item_name = item.name;
        newItems[index].unit_price = item.cost || 0;
      }
    }
    
    if (field === 'quantity' || field === 'unit_price') {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const resetForm = () => {
    setFormData({
      supplier_id: '',
      items: [{ _uid: crypto.randomUUID(), item_id: '', item_name: '', quantity: 1, unit_price: 0, total: 0 }],
      notes: '',
      expected_delivery: ''
    });
  };

  const filteredOrders = purchaseOrders.filter(po => {
    const matchesSearch = po.po_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         po.supplier_name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800',
      ordered: 'bg-blue-100 text-blue-800',
      received: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || colors.draft;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">Loading purchase orders...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Purchase Orders</h1>
          <p className="text-gray-600 mt-1">Manage inventory purchase orders</p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-500 hover:bg-amber-600">
              <Plus className="w-4 h-4 mr-2" />
              Create PO
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Purchase Order</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreatePO} className="space-y-4 mt-4">
              <div>
                <Label>Supplier *</Label>
                <Select value={formData.supplier_id} onValueChange={(val) => setFormData({...formData, supplier_id: val})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent className="z-[100]">
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Items *</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-1" /> Add Item
                  </Button>
                </div>
                
                {formData.items.map((item, idx) => (
                  <div key={item._uid} className="border rounded-lg p-3 mb-2 space-y-2">
                    <div className="grid grid-cols-12 gap-2">
                      <div className="col-span-5">
                        <Label className="text-xs">Item</Label>
                        <Select value={item.item_id} onValueChange={(val) => updateItem(idx, 'item_id', val)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select item" />
                          </SelectTrigger>
                          <SelectContent className="z-[100]">
                            {inventory.map(inv => (
                              <SelectItem key={inv.id} value={inv.id}>{inv.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Quantity</Label>
                        <Input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => updateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Unit Price</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Total</Label>
                        <Input value={`$${item.total.toFixed(2)}`} disabled />
                      </div>
                      <div className="col-span-1 flex items-end">
                        {formData.items.length > 1 && (
                          <Button type="button" size="sm" variant="ghost" onClick={() => removeItem(idx)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div>
                <Label>Expected Delivery</Label>
                <Input
                  type="date"
                  value={formData.expected_delivery}
                  onChange={(e) => setFormData({...formData, expected_delivery: e.target.value})}
                />
              </div>
              
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Additional notes..."
                />
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-lg font-semibold">
                  Total: ${formData.items.reduce((sum, item) => sum + item.total, 0).toFixed(2)}
                </div>
                <div className="space-x-2">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-amber-500 hover:bg-amber-600">
                    Create Purchase Order
                  </Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <Input
            placeholder="Search by PO number or supplier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="ordered">Ordered</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Purchase Orders List */}
      {filteredOrders.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Package className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No purchase orders found</p>
          <p className="text-sm text-gray-500 mt-1">Create your first purchase order to get started</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700">PO Number</th>
                <th className="text-left p-4 font-semibold text-gray-700">Supplier</th>
                <th className="text-left p-4 font-semibold text-gray-700">Total</th>
                <th className="text-left p-4 font-semibold text-gray-700">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700">Expected Delivery</th>
                <th className="text-left p-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map((po) => (
                <tr key={po.id} className="border-b hover:bg-gray-50">
                  <td className="p-4 font-medium">{po.po_number}</td>
                  <td className="p-4">{po.supplier_name}</td>
                  <td className="p-4 font-semibold">${po.total.toFixed(2)}</td>
                  <td className="p-4">
                    <Select value={po.status} onValueChange={(val) => handleUpdateStatus(po.id, val)}>
                      <SelectTrigger className={`w-32 ${getStatusColor(po.status)}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="ordered">Ordered</SelectItem>
                        <SelectItem value="received">Received</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    {po.expected_delivery ? new Date(po.expected_delivery).toLocaleDateString() : 'N/A'}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedPO(po);
                          setViewDialogOpen(true);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {po.status !== 'received' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePO(po.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Purchase Order Details</DialogTitle>
          </DialogHeader>
          {selectedPO && (
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-600">PO Number</Label>
                  <p className="font-semibold">{selectedPO.po_number}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Status</Label>
                  <p className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedPO.status)}`}>
                    {selectedPO.status.toUpperCase()}
                  </p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Supplier</Label>
                  <p className="font-semibold">{selectedPO.supplier_name}</p>
                </div>
                <div>
                  <Label className="text-sm text-gray-600">Expected Delivery</Label>
                  <p>{selectedPO.expected_delivery ? new Date(selectedPO.expected_delivery).toLocaleDateString() : 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-sm text-gray-600 mb-2 block">Items</Label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2 text-sm">Item</th>
                        <th className="text-right p-2 text-sm">Qty</th>
                        <th className="text-right p-2 text-sm">Unit Price</th>
                        <th className="text-right p-2 text-sm">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPO.items.map((item, idx) => (
                        <tr key={`${item.item_id || 'item'}-${idx}`} className="border-t">
                          <td className="p-2">{item.item_name}</td>
                          <td className="p-2 text-right">{item.quantity}</td>
                          <td className="p-2 text-right">${item.unit_price.toFixed(2)}</td>
                          <td className="p-2 text-right font-semibold">${item.total.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="flex justify-between items-center text-lg font-bold">
                  <span>Total</span>
                  <span>${selectedPO.total.toFixed(2)}</span>
                </div>
              </div>
              
              {selectedPO.notes && (
                <div>
                  <Label className="text-sm text-gray-600">Notes</Label>
                  <p className="text-sm mt-1">{selectedPO.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default PurchaseOrdersPage;